import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { websiteApi } from "../../../api/websiteApi";
import { paymentApi } from "../../../api/paymentApi";
import { useNotifications } from "../../../context/NotificationContext";
import "./RequestStatus.css";

const STATUS_LABELS = {
  pending: "Pending review",
  approved: "Approved",
  rejected: "Rejected",
  in_progress: "In progress",
  ready_for_review: "Ready for review",
  live: "Live",
  suspended: "Suspended",
};

const parseJson = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const WebsiteRequestStatus = () => {
  const { id } = useParams();
  const { socket } = useNotifications();
  const [request, setRequest] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState("");
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const checkoutForm = useRef(null);

  const loadRequest = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const response = await websiteApi.getClientRequest(id);
      setRequest(response.data);
      setError("");
    } catch (loadError) {
      setError(
        loadError.response?.data?.error || "Could not load this website request.",
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadRequest(true);
    paymentApi.getMethods()
      .then((response) => {
        const methods = response.data?.data ?? response.data ?? [];
        setPaymentMethods(Array.isArray(methods) ? methods : []);
        setSelectedMethod(
          methods.find?.((method) => method.code === "eft")?.code ||
            methods[0]?.code ||
            "",
        );
      })
      .catch((loadError) => {
        console.error("Could not load website payment methods:", loadError);
      });
  }, [loadRequest]);

  useEffect(() => {
    if (!socket) return undefined;
    const refresh = (event) => {
      if (event?.websiteRequestId === id) loadRequest();
    };
    socket.on("website-request:updated", refresh);
    return () => socket.off("website-request:updated", refresh);
  }, [id, loadRequest, socket]);

  useEffect(() => {
    if (checkout && checkoutForm.current) checkoutForm.current.requestSubmit();
  }, [checkout]);

  const startPayment = async () => {
    if (!selectedMethod || paying) return;
    setPaying(true);
    setError("");
    try {
      const response = await paymentApi.startWebsitePayment(id, selectedMethod);
      const paymentData = response.data?.data ?? response.data;
      if (paymentData.checkout) {
        setCheckout(paymentData.checkout);
      } else {
        await loadRequest();
      }
    } catch (paymentError) {
      setError(
        paymentError.response?.data?.error || "Could not start payment.",
      );
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <main className="request-status-page" role="status">
        Loading website request...
      </main>
    );
  }

  if (!request) {
    return (
      <main className="request-status-page">
        <p role="alert">{error || "Website request not found."}</p>
        <Link to="/dashboard">Back to dashboard</Link>
      </main>
    );
  }

  const domain = parseJson(request.DomainData);
  const invoice = parseJson(request.InvoiceData);
  const paymentDetails = parseJson(request.LatestPaymentData);
  const status = String(request.Status || "pending").toLowerCase();
  const isQuoteAwaiting = Boolean(invoice.quoteRequired);
  const canPay =
    !isQuoteAwaiting &&
    request.PaymentStatus !== "completed" &&
    !["rejected", "suspended", "live"].includes(status);
  const paymentMethod = paymentMethods.find(
    (method) => method.code === selectedMethod,
  );

  return (
    <main className="request-status-page">
      <header className="request-status-header">
        <div>
          <h2>Website project status</h2>
          <p>
            {request.BusinessName} · {request.PackageName}
          </p>
        </div>
        <span className={`request-badge request-badge--${status}`}>
          <span className="request-badge-dot" />
          {STATUS_LABELS[status] || status}
        </span>
      </header>

      {error && <p className="website-onboarding-alert error" role="alert">{error}</p>}

      <section className="request-status-card">
        <h3>Project details</h3>
        <dl className="request-detail-list">
          <div className="request-detail-row">
            <dt>Domain</dt>
            <dd>
              {domain.domainType === "custom"
                ? `${domain.customDomain || ""}${domain.selectedExtension || ""}`
                : domain.domainType === "existing"
                  ? domain.existingDomain
                  : `${domain.subdomain || ""}.tooraflex.co.za`}
            </dd>
          </div>
          <div className="request-detail-row">
            <dt>Submitted</dt>
            <dd>{new Date(request.CreatedAt).toLocaleString()}</dd>
          </div>
          <div className="request-detail-row">
            <dt>Last updated</dt>
            <dd>{new Date(request.UpdatedAt || request.CreatedAt).toLocaleString()}</dd>
          </div>
        </dl>
      </section>

      <section className="request-status-card">
        <h3>Invoice & payment</h3>
        <dl className="request-detail-list">
          <div className="request-detail-row">
            <dt>Invoice number</dt>
            <dd>{request.InvoiceNumber || invoice.invoiceNumber || "—"}</dd>
          </div>
          <div className="request-detail-row">
            <dt>Total</dt>
            <dd>
              {invoice.totalDue == null
                ? "Custom quote"
                : `R${Number(invoice.totalDue).toFixed(2)}`}
            </dd>
          </div>
          <div className="request-detail-row">
            <dt>Payment status</dt>
            <dd>{request.PaymentStatus || (isQuoteAwaiting ? "Awaiting quote" : "Not paid")}</dd>
          </div>
          {request.LatestPaymentReference && (
            <div className="request-detail-row">
              <dt>Payment reference</dt>
              <dd>{request.LatestPaymentReference}</dd>
            </div>
          )}
        </dl>
        {canPay && (
          <p className="request-payment-instructions">
            Payment is required before website work can begin. You can pay this
            invoice now or return to this page when you are ready.
          </p>
        )}
        {paymentDetails.instructions && (
          <p className="request-payment-instructions">
            {paymentDetails.instructions}
          </p>
        )}
        {canPay && paymentMethods.length > 0 && (
          <div className="request-status-payment-action">
            <label>
              <span>Payment method</span>
              <select
                value={selectedMethod}
                onChange={(event) => setSelectedMethod(event.target.value)}
              >
                {paymentMethods.map((method) => (
                  <option key={method.code} value={method.code}>
                    {method.displayName}
                  </option>
                ))}
              </select>
            </label>
            {paymentMethod?.instructions && <p>{paymentMethod.instructions}</p>}
            <button
              type="button"
              onClick={startPayment}
              disabled={paying || !selectedMethod || !paymentMethods.length}
            >
              {paying ? "Starting payment..." : "Pay invoice"}
            </button>
          </div>
        )}
        {canPay && paymentMethods.length === 0 && (
          <p className="website-onboarding-alert error" role="alert">
            Payment methods are currently unavailable. Please contact support
            to arrange payment.
          </p>
        )}
      </section>

      {request.ContractContent && (
        <details className="request-status-card">
          <summary>View accepted agreement</summary>
          <pre>{request.ContractContent}</pre>
        </details>
      )}

      <div className="request-status-actions">
        <Link to="/dashboard">Back to dashboard</Link>
      </div>

      {checkout && (
        <form
          ref={checkoutForm}
          method="post"
          action={checkout.action}
          className="gateway-checkout-form"
          aria-hidden="true"
        >
          {Object.entries(checkout.fields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
        </form>
      )}
    </main>
  );
};

export default WebsiteRequestStatus;
